import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CertificateStatusEnum, IUserCertificate } from '@share/common-types';
import { getUserCertificatesAsync } from '@share/api';
import { AppThunk } from '@share/utils';

export interface ICertificatesWalletState {
  data: IUserCertificate[];
  loading: boolean;
  error: string;
}

interface IFetchCertificatesWalletParams {
  email?: string;
  organizationId?: number;
  statuses?: CertificateStatusEnum[];
}

const initialState: ICertificatesWalletState = {
  data: [],
  loading: false,
  error: '',
};

const certificatesWalletSlice = createSlice({
  name: 'certificatesWallet',
  initialState,
  reducers: {
    setWalletCertificates: (
      state: ICertificatesWalletState,
      { payload }: PayloadAction<IUserCertificate[]>,
    ) => {
      state.data = payload;
    },
    setWalletCertificatesLoading: (
      state: ICertificatesWalletState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.loading = payload;
    },
    setWalletCertificatesError: (
      state: ICertificatesWalletState,
      { payload }: PayloadAction<string>,
    ) => {
      state.error = payload;
    },
    resetWalletCertificates: () => {
      return initialState;
    },
  },
});

export const certificatesWalletActions = certificatesWalletSlice.actions;
export const certificatesWalletReducer = certificatesWalletSlice.reducer;

export const fetchCertificatesWallet = (params: IFetchCertificatesWalletParams): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(certificatesWalletActions.setWalletCertificatesLoading(true));
      dispatch(certificatesWalletActions.setWalletCertificatesError(''));

      const { data } = await getUserCertificatesAsync({
        email: params.email,
        organizationId: params.organizationId,
        statuses: params.statuses,
        paging: { pageNumber: 1, pageSize: 100 },
      });
      dispatch(certificatesWalletActions.setWalletCertificates(data?.items ?? []));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(certificatesWalletActions.setWalletCertificatesError(message));
      dispatch(certificatesWalletActions.setWalletCertificates([]));
    } finally {
      dispatch(certificatesWalletActions.setWalletCertificatesLoading(false));
    }
  };
};
